import { Box, Button, Flex, Grid } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Stages, useCartridgeCreatorProvider } from 'src/providers/Cartridge/cartridgeProvider';
import { ContentContainer } from '../../ContentContainer/contentContainer';
import { FORM_CONFIG } from '../formConfig';
import { CartridgeInfoType } from '../Pages/SetCartridgeType/setCartridgeType';

interface Props {
	setCurrentStep: (step: number) => void;
	currentStep: number;
	isValid: () => Promise<boolean>;
	currentSchema: any;
	currentCartridge: CartridgeInfoType;
}

export const StepNavigation: FC<Props> = ({
	currentSchema,
	isValid,
	setCurrentStep,
	currentStep,
	currentCartridge,
}) => {
	const { t } = useTranslation();
	const { setFieldTouched, values } = useFormikContext();
	const { stage } = useCartridgeCreatorProvider();
	const navigate = useNavigate();

	const [stepBlocked, setStepBlocked] = useState<boolean>(true);

	const checkValid = async () => {
		let valid = await isValid();

		setStepBlocked(valid ? !valid : true);
	};

	useEffect(() => {
		checkValid();
	}, [values, currentStep]);

	// set form fields touched, so they display errors if present
	const checkTouched = (fields: any, prevValue: string) => {
		Object.keys(fields).forEach((val: string) => {
			if (typeof fields[val] === 'object' && fields[val].fields && Object.keys(fields[val].fields).length > 0) {
				checkTouched(fields[val].fields, `${prevValue.length > 0 ? `${prevValue}.` : ''}${val}`);
			} else {
				setFieldTouched(`${prevValue.length > 0 ? `${prevValue}.` : ''}${val}`, true);
			}
		});
	};

	const minStep = useMemo(() => {
		if (stage >= Stages.FINISHED) return 6;
		if (stage >= Stages.IGO_SET) return 5;
		if (stage >= Stages.DEPLOYED) return 4;
		if (stage >= Stages.DEPLOYING) return 3;
		if (stage >= Stages.UNDEPLOYED) return 1;
		return 1;
	}, [stage]);

	const nextStep = async () => {
		checkTouched(currentSchema.fields, '');
		const valid = await isValid();
		if (!valid) return;

		const nextStep = currentStep + 1;

		if (nextStep <= 50) {
			if (currentStep < FORM_CONFIG.FORM_PAGES_COUNT) {
				setCurrentStep(nextStep);
				window.scrollTo(0, 0);
			} else {
				navigate('/creator/games');
			}
		}
	};

	const prevStep = () => {
		const prevStep = currentStep - 1;
		if (prevStep >= 1 && prevStep >= minStep) {
			setCurrentStep(prevStep);
			window.scrollTo(0, 0);
		}
	};

	const nextStepName = useMemo(
		() => (currentStep === FORM_CONFIG.FORM_PAGES_COUNT ? t('form.buttons.finish') : t('form.buttons.next')),
		[currentStep]
	);

	return (
		<ContentContainer noPadding>
			<Grid
				pos="relative"
				w="100%"
			>
				<Flex
					mt="40px"
					w="100%"
					position="relative"
					justifyContent="space-between"
				>
					<Box>
						{currentStep > minStep && (
							<Button
								onClick={prevStep}
								variant="ghost"
							>
								{t('form.buttons.back')}
							</Button>
						)}
					</Box>
					<Box>
						<Button
							disabled={stepBlocked}
							onClick={nextStep}
							cursor={false ? 'default' : 'pointer'}
							opacity={false ? '0.5' : '1'}
						>
							{nextStepName}
						</Button>
					</Box>
				</Flex>
			</Grid>
		</ContentContainer>
	);
};
